/* GENERADO — no editar a mano.
 *
 * Derivado de flow/src/components/shared/blocks/ProductBand/ProductBand.tsx quitandole los tipos, para
 * que la previsualizacion del back office editorial monte los MISMOS
 * componentes que renderiza /historias/[slug] en el sitio publico.
 *
 * Se regenera con:                 npm run historia:sync
 * Se verifica (falla si difiere):   npm run historia:diff
 *
 * Un cambio en el componente de flow se trae con `historia:sync`. Editar este
 * archivo directamente lo pierde en la siguiente regeneracion — y peor: hace
 * que la previsualizacion muestre algo distinto de lo que se publica, que es
 * justo lo que esa pantalla existe para evitar.
 *
 * La salida definitiva es publicar estos componentes en el Design System y que
 * los dos repos los consuman; ver
 * docs/superpowers/specs/2026-08-26-editorial-design-system-design.md §5.
 */
import React from 'react';
import { renderHighlighted } from './highlight';
import { Button } from './Button';

/* FancyYool highlight tokens (text-only, weight 700). Light variants for
   light backgrounds; `dark-*` variants for navy/photo/dark backgrounds. */

/* Where the text block sits inside the band. */

/* Legibility scrim. `none` = clean photo, no wash. */

const OVERLAYS = {
  none: undefined,
  'dark-top': 'linear-gradient(180deg, rgba(10,20,32,.62) 0%, rgba(10,20,32,.34) 26%, rgba(10,20,32,.06) 46%, rgba(10,20,32,0) 64%)',
  'dark-bottom': 'linear-gradient(0deg, rgba(10,20,32,.66) 0%, rgba(10,20,32,.36) 28%, rgba(10,20,32,.06) 54%, rgba(10,20,32,0) 78%)',
  'light-bottom': 'linear-gradient(0deg, rgba(247,247,247,.97) 0%, rgba(247,247,247,.88) 24%, rgba(247,247,247,.4) 48%, rgba(247,247,247,0) 72%)',
  'light-right': 'linear-gradient(270deg, rgba(247,247,247,.97) 0%, rgba(247,247,247,.9) 30%, rgba(247,247,247,.42) 56%, rgba(247,247,247,0) 80%)',
  'light-left': 'linear-gradient(90deg, rgba(247,247,247,.97) 0%, rgba(247,247,247,.9) 30%, rgba(247,247,247,.42) 56%, rgba(247,247,247,0) 80%)',
  'navy-bottom': 'linear-gradient(8deg, rgba(14,38,64,.94) 0%, rgba(14,38,64,.82) 26%, rgba(14,38,64,.34) 52%, rgba(14,38,64,.06) 78%)'
};
function resolveBg(bg) {
  if (!bg || bg === 'light') return 'var(--yool-page)';
  if (bg === 'navy') return 'var(--yool-navy)';
  return bg;
}
function alignFor(pos) {
  if (pos.endsWith('center') || pos === 'center') return 'center';
  if (pos.endsWith('right')) return 'right';
  return 'left';
}
function headBox(pos) {
  const align = alignFor(pos);
  const base = {
    position: 'relative',
    zIndex: 2,
    maxWidth: 560,
    textAlign: align
  };
  switch (pos) {
    case 'top-left':
      return {
        ...base,
        padding: '40px 32px 0'
      };
    case 'top-center':
      return {
        ...base,
        padding: '40px 28px 0',
        marginLeft: 'auto',
        marginRight: 'auto'
      };
    case 'top-right':
      return {
        ...base,
        padding: '44px 32px 0 28px',
        marginLeft: 'auto'
      };
    case 'bottom-left':
      return {
        ...base,
        position: 'absolute',
        left: 0,
        bottom: 0,
        padding: '0 28px 34px 36px'
      };
    case 'bottom-center':
      return {
        ...base,
        position: 'absolute',
        left: '50%',
        bottom: 0,
        transform: 'translateX(-50%)',
        padding: '0 28px 34px'
      };
    case 'bottom-right':
      return {
        ...base,
        position: 'absolute',
        right: 0,
        bottom: 0,
        padding: '0 36px 34px 28px'
      };
    case 'center':
      return {
        ...base,
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 40
      };
    default:
      return base;
  }
}
export const ProductBand = ({
  eyebrow,
  title,
  highlight,
  highlightColor = 'cyan',
  subtitle,
  image,
  imagePosition = 'center 50%',
  mirror = false,
  background = 'light',
  textPosition = 'top-center',
  overlay = 'none',
  onDark = false,
  size = 'standard',
  height,
  primaryLabel,
  onPrimary,
  secondaryLabel,
  onSecondary,
  children,
  animateTitle = false
}) => {
  const SIZE_HEIGHT = {
    hero: 540,
    standard: 480,
    compact: 360
  };
  /* Escala de display tomada de los tokens del sistema (--fs-display-*). */
  const SIZE_TITLE = {
    hero: '700 var(--fs-display-lg)/1.02 var(--font-sans)',
    standard: '700 var(--fs-display-sm)/1.04 var(--font-sans)',
    compact: '700 clamp(22px,2.4vw,30px)/1.1 var(--font-sans)'
  };
  const gsl = animateTitle === true ? 'scroll' : animateTitle || undefined;
  const minH = height === undefined || height === null || height === '' ? SIZE_HEIGHT[size] : Number(height);
  const titleFont = SIZE_TITLE[size];
  const align = alignFor(textPosition);
  const justify = align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start';
  const ink = onDark ? '#fff' : 'var(--yool-black)';
  const eyebrowInk = onDark ? 'rgba(255,255,255,.66)' : 'var(--yool-muted)';
  const subInk = onDark ? 'rgba(255,255,255,.84)' : 'var(--yool-gray1)';
  const gradient = OVERLAYS[overlay];
  return <section data-gsl-photo={gsl && image ? '1' : undefined} style={{
    position: 'relative',
    overflow: 'hidden',
    borderRadius: 20,
    minHeight: minH,
    background: resolveBg(background),
    boxShadow: 'var(--shadow-sm)'
  }}>
      {image && <div className="gsl-photo-wrap" style={{
      position: 'absolute',
      inset: 0,
      overflow: 'hidden'
    }}>
          <img src={image} alt="" aria-hidden="true" style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        objectPosition: imagePosition,
        transform: mirror ? 'scaleX(-1)' : undefined
      }} />
        </div>}
      {gradient && <div aria-hidden="true" style={{
      position: 'absolute',
      inset: 0,
      background: gradient
    }} />}
      <div style={headBox(textPosition)}>
        {eyebrow && <div style={{
        font: '600 11px/1 var(--font-sans)',
        letterSpacing: '.1em',
        textTransform: 'uppercase',
        color: eyebrowInk,
        marginBottom: 12
      }}>
            {eyebrow}
          </div>}
        <h3 data-gsl={gsl} style={{
        margin: 0,
        font: titleFont,
        letterSpacing: '-0.04em',
        color: ink
      }}>
          {renderHighlighted(title, highlight, highlightColor)}
        </h3>
        {subtitle && <p style={{
        margin: '14px 0 0',
        font: '400 16px/1.55 var(--font-sans)',
        color: subInk,
        maxWidth: '46ch',
        marginLeft: align === 'right' ? 'auto' : 0
      }}>
            {subtitle}
          </p>}
        {(primaryLabel || secondaryLabel) && <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 10,
        marginTop: 22,
        justifyContent: justify
      }}>
            {primaryLabel && <Button variant="primary" onClick={onPrimary}>
                {primaryLabel}
              </Button>}
            {secondaryLabel && <Button variant="outline" onDark={onDark} onClick={onSecondary}>
                {secondaryLabel}
              </Button>}
          </div>}
      </div>
      {children}
    </section>;
};
export default ProductBand;
